import React from 'react';
import {
  X, Loader2, FileBarChart2, Info, FileText, Building2, Target, CheckSquare, BarChart3, TrendingUp,
  DollarSign, FileCheck, Clock
} from 'lucide-react';
import { NaturezaDespesa } from '../types';
import { DIRETRIZES } from '../constants';
import { justDisplayText } from '../utils/justificativa';
import { EscParaFechar } from './Feedback';

interface VisualizarPlanoModalProps {
  viewingPlanData: any;
  isLoadingViewPlan: boolean;
  dynamicNaturezas: NaturezaDespesa[];
  onClose: () => void;
}

// Visualização somente leitura de um plano salvo (lista de planos → "ver")
export const VisualizarPlanoModal: React.FC<VisualizarPlanoModalProps> = ({ viewingPlanData, isLoadingViewPlan, dynamicNaturezas, onClose }) => (
    <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Visualizar plano de trabalho">
      <EscParaFechar onClose={onClose} />
      <div className="bg-white rounded-2xl w-full max-w-5xl shadow-2xl flex flex-col max-h-[95vh] overflow-hidden animate-fadeIn">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-5 flex items-center justify-between flex-shrink-0 border-b-4 border-red-600">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-600/20">
              <FileBarChart2 className="text-red-400 w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-black text-white">Visualizar Plano de Trabalho</h2>
              <p className="text-xs text-gray-400 mt-0.5">Modo leitura — sem alterações</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Fechar visualização"
            className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400 hover:text-white" />
          </button>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50">
          {isLoadingViewPlan ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <Loader2 className="w-12 h-12 text-red-600 animate-spin" />
              <p className="text-gray-500 font-semibold">Carregando plano...</p>
            </div>
          ) : viewingPlanData ? (() => {
            const { plano, acoes, metas, naturezas } = viewingPlanData;
            const valorTotal = parseFloat(plano.valor_total) || 0;
            const totalAcoes = acoes.reduce((s: number, a: any) => s + (parseFloat(a.valor) || 0), 0);
            const totalNaturezas = naturezas.reduce((s: number, n: any) => s + (parseFloat(n.valor) || 0), 0);
            const diretriz = DIRETRIZES.find(d => d.id === plano.diretriz_id);
            const objetivo = diretriz?.objetivos.find(o => o.id === plano.objetivo_id);
            return (
              <div className="space-y-5">
                {/* Status bar */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white rounded-xl p-4 border border-gray-200 text-center shadow-sm">
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Valor Total</p>
                    <p className="text-lg font-black text-red-600">R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 text-center shadow-sm">
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Ações Cadastradas</p>
                    <p className="text-2xl font-black text-gray-900">{acoes.length}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 text-center shadow-sm">
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Edições</p>
                    <p className="text-2xl font-black text-orange-600">{plano.edit_count || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 text-center shadow-sm">
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Criado em</p>
                    <p className="text-sm font-black text-gray-900">{new Date(plano.created_at).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>

                {/* Seção 1: Info Emenda */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-red-600 px-5 py-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-white" />
                    <h3 className="text-sm font-black text-white uppercase tracking-wider">1. Identificação da Emenda</h3>
                  </div>
                  <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase">Parlamentar</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{plano.parlamentar || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase">Nº da Emenda</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5 font-mono">{plano.numero_emenda || '—'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs text-gray-500 font-semibold uppercase">Programa</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{plano.programa || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Seção 2: Beneficiário */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-blue-600 px-5 py-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-white" />
                    <h3 className="text-sm font-black text-white uppercase tracking-wider">2. Beneficiário</h3>
                  </div>
                  <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-3">
                      <p className="text-xs text-gray-500 font-semibold uppercase">Razão Social</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{plano.beneficiario_nome || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase">CNPJ</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5 font-mono">{plano.beneficiario_cnpj || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase">CNES</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5 font-mono">{plano.cnes || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase">Conta Bancária</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5 font-mono">{plano.conta_bancaria || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase">Email</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{plano.beneficiario_email || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase">Telefone</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{plano.beneficiario_telefone || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Seção 3: Alinhamento */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-green-600 px-5 py-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-white" />
                    <h3 className="text-sm font-black text-white uppercase tracking-wider">3. Alinhamento Estratégico</h3>
                  </div>
                  <div className="p-5 space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase">Diretriz</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{diretriz ? `${diretriz.numero}. ${diretriz.titulo}` : (plano.diretriz_id || '—')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase">Objetivo Estratégico</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{objetivo ? objetivo.titulo : (plano.objetivo_id || '—')}</p>
                    </div>
                    {Array.isArray(plano.metas_ids) && plano.metas_ids.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Metas Relacionadas ({plano.metas_ids.length})</p>
                        <div className="flex flex-wrap gap-1.5">
                          {plano.metas_ids.map((metaId: string) => {
                            const metaObj = objetivo?.metas?.find((m: any) => m.id === metaId);
                            return (
                              <span key={metaId} className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 font-semibold px-2 py-1 rounded-full border border-green-200">
                                <CheckSquare className="w-3 h-3" />
                                {metaObj?.descricao || metaId}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Seção 4: Metas Quantitativas */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-amber-500 px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-white" />
                      <h3 className="text-sm font-black text-white uppercase tracking-wider">4. Metas Quantitativas</h3>
                    </div>
                    <span className="text-xs font-bold text-amber-100">Total: R$ {totalAcoes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                  {acoes.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Categoria</th>
                            <th className="px-4 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Ação / Serviço</th>
                            <th className="px-4 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Valor</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {acoes.map((a: any, i: number) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-4 py-2.5 text-xs text-gray-600 font-medium">{a.categoria}</td>
                              <td className="px-4 py-2.5 text-xs font-bold text-gray-900">{a.item}</td>
                              <td className="px-4 py-2.5 text-xs font-black text-red-600 text-right">R$ {(parseFloat(a.valor) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center text-sm text-gray-400 py-6">Nenhuma ação registrada</p>
                  )}
                </div>

                {/* Seção 5: Metas Qualitativas */}
                {metas.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="bg-purple-600 px-5 py-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-white" />
                      <h3 className="text-sm font-black text-white uppercase tracking-wider">5. Indicadores Qualitativos</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Indicador</th>
                            <th className="px-4 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Meta (%)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {metas.map((m: any, i: number) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-4 py-2.5 text-xs font-bold text-gray-900">{m.meta_descricao}</td>
                              <td className="px-4 py-2.5 text-xs font-black text-purple-600 text-right">{m.indicador}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Seção 6: Naturezas de Despesa */}
                {naturezas.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="bg-teal-600 px-5 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-white" />
                        <h3 className="text-sm font-black text-white uppercase tracking-wider">6. Execução Financeira</h3>
                      </div>
                      <span className="text-xs font-bold text-teal-100">Total: R$ {totalNaturezas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Natureza de Despesa</th>
                            <th className="px-4 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Valor</th>
                            <th className="px-4 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">% do Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {naturezas.map((n: any, i: number) => {
                            const nat = dynamicNaturezas.find((nd: any) => nd.codigo === n.codigo);
                            const pct = valorTotal > 0 ? ((parseFloat(n.valor) || 0) / valorTotal * 100) : 0;
                            return (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-4 py-2.5 text-xs font-bold text-gray-900">
                                  <span className="font-mono text-gray-500 mr-2">{n.codigo}</span>
                                  {nat ? (nat as any).descricao : n.codigo}
                                </td>
                                <td className="px-4 py-2.5 text-xs font-black text-teal-600 text-right">R$ {(parseFloat(n.valor) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                <td className="px-4 py-2.5 text-xs text-gray-600 text-right">{pct.toFixed(1)}%</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Seção 7: Justificativa */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-gray-700 px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileCheck className="w-4 h-4 text-white" />
                      <h3 className="text-sm font-black text-white uppercase tracking-wider">7. Justificativa Técnica</h3>
                    </div>
                    {plano.justificativa_alterada_em && (
                      <span className="inline-flex items-center gap-1.5 text-xs bg-purple-500/30 text-purple-200 font-bold px-2.5 py-1 rounded-full">
                        <Clock className="w-3 h-3" />
                        Alterada em {new Date(plano.justificativa_alterada_em).toLocaleDateString('pt-BR')} às {new Date(plano.justificativa_alterada_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div className="p-5 space-y-4">
                    <div className="border-l-4 border-red-600 bg-gray-50 pl-4 pr-3 py-3 text-sm leading-relaxed text-gray-900 whitespace-pre-wrap break-words rounded-r-lg max-h-64 overflow-y-auto">
                      {justDisplayText(plano.justificativa) || '—'}
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{justDisplayText(plano.justificativa || '').length.toLocaleString('pt-BR')} caracteres</span>
                      <span>Responsável: <strong className="text-gray-700">{plano.responsavel_assinatura || '—'}</strong></span>
                    </div>
                  </div>
                </div>

                {/* Rodapé */}
                <div className="bg-gray-100 rounded-xl p-4 border border-gray-200 text-xs text-gray-500 flex flex-wrap gap-4 justify-between">
                  <span>Criado por: <strong className="text-gray-700">{plano.created_by_name || '—'}</strong> ({plano.created_by_email || '—'})</span>
                  <span>Última edição: <strong className="text-gray-700">{plano.last_edited_at ? new Date(plano.last_edited_at).toLocaleString('pt-BR') : '—'}</strong></span>
                  <span>Total de edições: <strong className="text-gray-700">{plano.edit_count || 0}</strong></span>
                </div>
              </div>
            );
          })() : null}
        </div>
        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-200 px-6 py-4 bg-white flex justify-end">
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-6 py-2.5 bg-gray-800 text-white rounded-xl font-bold text-sm hover:bg-gray-700 transition-all"
          >
            <X className="w-4 h-4" /> Fechar
          </button>
        </div>
      </div>
    </div>
);
